/* -----------------Globals--------------- */
import { useMemo, useState } from "react";
import { Platform } from "react-native";

/* -----------------Helpers & Hooks--------------- */
import * as Clipboard from "expo-clipboard";

/* -----------------Child components--------------- */
import { FeedbackDialog } from "../../feedback";

import { useTranslation } from "react-i18next";
import type { FeedbackParams } from "../../feedback";
/* -----------------Types--------------- */
import type { HandleTracking } from "../../types";

import Box from "@memorang/ui/src/components/Box";
import type { MenuAction } from "@memorang/ui/src/components/ChatUI/types";
import CustomSnackbar from "@memorang/ui/src/components/CustomSnackbar";
import { useDevice } from "@memorang/ui/src/useDevice";
import { router } from "expo-router";
/* -----------------Constants--------------- */
import { events, ENDPOINT_RESEND_EMAIL } from "../constants";

type Props = {
	handleTracking: HandleTracking;
	searchQuery?: string;
	feedbackDialogParams?: FeedbackParams;
};

export const useMessageActions = ({
	handleTracking,
	searchQuery,
	feedbackDialogParams,
}: Props) => {
	const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
	const [showCopyFeedback, setShowCopyFeedback] = useState(false);
	const [runId, setRunId] = useState<string | undefined>();
	const { isMobile } = useDevice();

	const [feedbackRatingType, setFeedbackRatingType] = useState<
		"positive" | "negative" | "reportIssue"
	>();

	const { t } = useTranslation();
	const toggleShowFeedbackDialog = (): void => {
		setShowFeedbackDialog((prev) => !prev);
	};

	const handleSendEmail = async (text?: string) => {
		await fetch(ENDPOINT_RESEND_EMAIL, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				examName: feedbackDialogParams?.context,
				markdown: text,
				toEmail: feedbackDialogParams?.email,
				companyName: feedbackDialogParams?.companyName,
			}),
		});
		handleTracking(events.emailSent);
	};

	const handleEmailIconPress = (text?: string) => {
		handleTracking(events.emailTriggered);
		handleSendEmail(text);
	};

	const handleCopyIconPress = async (text?: string) => {
		if (!text) {
			return;
		}

		const textToCopy = searchQuery
			? `# Question:\n${searchQuery}\n\n# Answer:\n${text}`
			: text;

		if (Platform.OS === "web") {
			navigator.clipboard.writeText(textToCopy);
		} else if (Platform.OS === "android" || Platform.OS === "ios") {
			await Clipboard.setStringAsync(textToCopy);
		}

		handleTracking(events.copilotResponseCopyClicked, {
			query: searchQuery ?? "",
			summary: text,
		});
		setShowCopyFeedback(true);
	};

	const handlePositiveFeedbackIconPress = (runId?: string) => {
		if (isMobile) {
			handleNavigateToFeedback("positive", runId);
		} else {
			setFeedbackRatingType("positive");
			setRunId(runId);
			toggleShowFeedbackDialog();
		}
		handleTracking(events.aiFeedbackPositive);
	};

	const handleNegativeFeedbackIconPress = (runId?: string) => {
		if (isMobile) {
			handleNavigateToFeedback("negative", runId);
		} else {
			setFeedbackRatingType("negative");
			setRunId(runId);
			toggleShowFeedbackDialog();
		}
		handleTracking(events.aiFeedbackNegative);
	};

	const handleReportIssueIconPress = (runId?: string) => {
		if (isMobile) {
			handleNavigateToFeedback("reportIssue", runId);
		} else {
			setFeedbackRatingType("reportIssue");
			setRunId(runId);
			toggleShowFeedbackDialog();
		}
		handleTracking(events.aiFeedbackReport);
	};

	const handleNavigateToFeedback = (
		feedbackRatingType?: "positive" | "negative" | "reportIssue",
		runId?: string,
	) => {
		router.replace({
			pathname: "/feedback",
			params: {
				view: feedbackDialogParams?.view ?? "session",
				featureName: feedbackDialogParams?.feature,
				feedbackTitle: t(
					feedbackRatingType === "reportIssue"
						? "dialog.report.title"
						: "report.sessionAnalysis.feedbackDialog.title",
				),
				feedbackSubtitle: t(
					feedbackRatingType === "reportIssue"
						? "dialog.report.body"
						: "report.sessionAnalysis.feedbackDialog.body",
				),
				itemFeedbackRating:
					feedbackRatingType === "reportIssue"
						? "negative"
						: feedbackRatingType,
				sessionId: feedbackDialogParams?.sessionId,
				runId,
				type:
					feedbackRatingType === "reportIssue" ? "Content Flagged" : undefined,
				placeholder: t("report.sessionAnalysis.feedbackDialog.placeholder"),
				feedbackCta:
					feedbackRatingType === "reportIssue"
						? t("dialog.report.cta")
						: undefined,
			},
		});
	};

	const messageActions = (
		showEmail?: boolean,
		runId?: string,
	): {
		icon: string;
		altText: string;
		tooltip: string;
		handleOnPress: (text?: string) => void;
	}[] => [
		...(showEmail
			? [
					{
						icon: "email-outline",
						tooltip: t("report.sessionAnalysis.menuActions.emailSummary"),
						altText: "Email",
						handleOnPress: handleEmailIconPress,
					},
				]
			: []),
		{
			icon: "content-copy",
			tooltip: t("report.sessionAnalysis.menuActions.copy"),
			altText: t("report.sessionAnalysis.menuActions.altCopy"),
			handleOnPress: handleCopyIconPress,
		},
		{
			icon: "thumb-up-outline",
			tooltip: t("report.sessionAnalysis.menuActions.positiveFeedback"),
			altText: t("report.sessionAnalysis.menuActions.altPositiveFeedback"),
			handleOnPress: () => handlePositiveFeedbackIconPress(runId),
		},
		{
			icon: "thumb-down-outline",
			tooltip: t("report.sessionAnalysis.menuActions.negativeFeedback"),
			altText: t("report.sessionAnalysis.menuActions.altNegativeFeedback"),
			handleOnPress: () => handleNegativeFeedbackIconPress(runId),
		},
		{
			icon: "flag-outline",
			tooltip: t("report.sessionAnalysis.menuActions.reportIssue"),
			altText: t("report.sessionAnalysis.menuActions.altReportIssue"),
			handleOnPress: () => handleReportIssueIconPress(runId),
		},
	];

	const appBarFeedbackAction: MenuAction = {
		icon: "comment-quote-outline",
		title: t("common.leaveFeedback"),
		handleAction: () => {
			if (isMobile) {
				handleNavigateToFeedback();
			} else {
				toggleShowFeedbackDialog();
			}
		},
	};

	const handlePostFeedback = (feedback: string, email: string) => {
		handleTracking(events.feedbackSubmitted, {
			type: feedbackRatingType!,
			feedback,
			email,
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const messageFeedbackComponents = useMemo(
		() => (
			<>
				{feedbackDialogParams && (
					<FeedbackDialog
						visible={showFeedbackDialog}
						onDismiss={toggleShowFeedbackDialog}
						handleTracking={handleTracking}
						handlePostFeedback={handlePostFeedback}
						feedbackDialogParams={{
							...feedbackDialogParams,
							runId,
							type:
								feedbackRatingType === "reportIssue"
									? "Content Flagged"
									: feedbackDialogParams.type,
							rating:
								feedbackRatingType === "reportIssue"
									? "negative"
									: feedbackRatingType,
						}}
						feedbackPlaceholder={t(
							"report.sessionAnalysis.feedbackDialog.placeholder",
						)}
						icon={
							feedbackRatingType === "reportIssue"
								? "flag-outline"
								: "comment-quote-outline"
						}
						title={t(
							feedbackRatingType === "reportIssue"
								? "dialog.report.title"
								: "report.sessionAnalysis.feedbackDialog.title",
						)}
						subtitle={t(
							feedbackRatingType === "reportIssue"
								? "dialog.report.body"
								: "report.sessionAnalysis.feedbackDialog.body",
						)}
						cta={
							feedbackRatingType === "reportIssue"
								? t("dialog.report.cta")
								: undefined
						}
					/>
				)}
				<Box position="absolute" bottom={54} left={0} right={0}>
					<CustomSnackbar
						visible={showCopyFeedback}
						message={t("common.copiedToClipboard")}
						handleDismiss={() => setShowCopyFeedback(false)}
					/>
				</Box>
			</>
		),
		[
			showFeedbackDialog,
			feedbackDialogParams,
			handleTracking,
			runId,
			showCopyFeedback,
			isMobile,
		],
	);

	return {
		messageActions,
		messageFeedbackComponents,
		appBarFeedbackAction,
	};
};
